import {
  SLOT_BUTTON_STATUSES,
  SLOT_COLOR_PARTS,
  SLOT_COLOR_FOLLOW_ACTIVE,
  SLOT_COLOR_BORDER_SHADE,
  SLOT_COLOR_HOVER_SHADE,
  SLOT_COLOR_HATCH_SHADE,
} from '../constants/bookingConst';
import { isValidHex, shadeHex, hexToRgba } from './colorUtil';

/**
 * Lower-cases and expands a hex colour ("#ABC" -> "#aabbcc") so two spellings
 * of the same colour compare equal.
 *
 * @param {string} hex - a value that already passed isValidHex
 * @returns {string} "#aabbcc"
 */
const normalizeHex = (hex) => {
  const clean = hex.trim().toLowerCase();
  return clean.length === 4 ? `#${clean.slice(1).split('').map((c) => c + c).join('')}` : clean;
};

/**
 * Looks up one slot state's definition.
 *
 * @param {string} statusKey - a SLOT_BUTTON_STATUSES key
 * @returns {object|undefined}
 */
const findStatus = (statusKey) => SLOT_BUTTON_STATUSES.find((status) => status.key === statusKey);

/**
 * The colour a slot state shows when the admin has not changed it.
 *
 * @param {string} statusKey - a SLOT_BUTTON_STATUSES key
 * @param {'background'|'text'} part
 * @param {string} activeColor - Config -> Theme -> Active Button Color, used
 *   by defaults that follow it (Selected)
 * @returns {string} "#aabbcc"
 */
export const getSlotColorDefault = (statusKey, part, activeColor) => {
  const value = findStatus(statusKey)?.defaults?.[part];
  return value === SLOT_COLOR_FOLLOW_ACTIVE ? activeColor : value;
};

/**
 * The colour a slot state actually shows: the admin's choice when it is a
 * valid hex, otherwise the default.
 *
 * @param {object|undefined} colors - config.slotButtonColors
 * @param {string} statusKey
 * @param {'background'|'text'} part
 * @param {string} activeColor
 * @returns {string}
 */
export const getEffectiveSlotColor = (colors, statusKey, part, activeColor) => {
  const stored = colors?.[statusKey]?.[part];
  return isValidHex(stored) ? stored.trim() : getSlotColorDefault(statusKey, part, activeColor);
};

/**
 * Whether a slot state's colour part follows the Active Button Color — true
 * when its default is SLOT_COLOR_FOLLOW_ACTIVE and no custom colour is saved.
 *
 * @param {object|undefined} colors - config.slotButtonColors
 * @param {string} statusKey
 * @param {'background'|'text'} part
 * @returns {boolean}
 */
export const followsActiveColor = (colors, statusKey, part) => (
  findStatus(statusKey)?.defaults?.[part] === SLOT_COLOR_FOLLOW_ACTIVE
  && !isValidHex(colors?.[statusKey]?.[part])
);

/**
 * Returns a copy of the colour map with one colour changed.
 *
 * A colour equal to its default is REMOVED rather than stored: the map then
 * only ever holds real customisations, so Reset is just an empty map.
 *
 * The exception is a part whose default follows the Active Button Color
 * (Selected's background). Following is switched on and off explicitly with
 * setFollowsActiveColor, so a custom colour is kept even when it happens to
 * match today's active colour — otherwise unticking "Use active button
 * color" and leaving the swatch as-is would silently tick it again.
 *
 * @param {object|undefined} colors - current config.slotButtonColors
 * @param {string} statusKey
 * @param {'background'|'text'} part
 * @param {string} value - a valid hex colour
 * @param {string} activeColor
 * @returns {object} the new map (never mutates the input)
 */
export const setSlotButtonColor = (colors, statusKey, part, value, activeColor) => {
  const next = { ...(colors || {}) };
  const entry = { ...(next[statusKey] || {}) };
  const followable = findStatus(statusKey)?.defaults?.[part] === SLOT_COLOR_FOLLOW_ACTIVE;
  const fallback = getSlotColorDefault(statusKey, part, activeColor);

  if (!followable && isValidHex(fallback) && normalizeHex(value) === normalizeHex(fallback)) {
    delete entry[part];
  } else {
    entry[part] = normalizeHex(value);
  }

  if (Object.keys(entry).length > 0) next[statusKey] = entry;
  else delete next[statusKey];
  return next;
};

/**
 * Turns "follow the Active Button Color" on or off for a followable part.
 * Turning it off pins the current active colour as the starting custom
 * colour, so the button looks the same until the admin picks another.
 *
 * @param {object|undefined} colors - config.slotButtonColors
 * @param {string} statusKey
 * @param {'background'|'text'} part
 * @param {boolean} follow
 * @param {string} activeColor - a valid hex colour
 * @returns {object} the new map (never mutates the input)
 */
export const setFollowsActiveColor = (colors, statusKey, part, follow, activeColor) => {
  if (!follow) return setSlotButtonColor(colors, statusKey, part, activeColor, activeColor);

  const next = { ...(colors || {}) };
  const entry = { ...(next[statusKey] || {}) };
  delete entry[part];
  if (Object.keys(entry).length > 0) next[statusKey] = entry;
  else delete next[statusKey];
  return next;
};

/**
 * CSS custom-property declarations for every customised slot colour, for the
 * dynamic theme stylesheet (ConfigContext). Only customised colours are
 * emitted; TimeSlotSelection.css falls back to the original values for the
 * rest, so an unconfigured club renders exactly as before.
 *
 * Borders, hover shades and the Closed hatch's second stripe are derived from
 * a custom background, because the admin picks one colour per part and the
 * originals were all a shade off their fill.
 *
 * @param {object|undefined} colors - config.slotButtonColors
 * @returns {string} declarations, one per line (empty when nothing is set)
 */
export const buildSlotColorCssVars = (colors) => {
  if (!colors || typeof colors !== 'object') return '';
  const lines = [];

  SLOT_BUTTON_STATUSES.forEach(({ key, cssKey }) => {
    const entry = colors[key] || {};
    const background = isValidHex(entry.background) ? entry.background.trim() : null;
    const text = isValidHex(entry.text) ? entry.text.trim() : null;

    if (background) {
      lines.push(`--jt-slot-${cssKey}-bg: ${background};`);
      // Selected and Open Play are solid fills whose border has always been
      // the fill itself; every other state carries a slightly darker edge.
      const solidFill = key === 'selected' || key === 'openPlay';
      lines.push(`--jt-slot-${cssKey}-border: ${solidFill ? background : shadeHex(background, SLOT_COLOR_BORDER_SHADE)};`);
      if (key === 'openPlay') {
        lines.push(`--jt-slot-${cssKey}-hover: ${shadeHex(background, SLOT_COLOR_HOVER_SHADE)};`);
        lines.push(`--jt-slot-${cssKey}-ring: ${hexToRgba(background, 0.25)};`);
      }
      if (key === 'unavailable') {
        lines.push(`--jt-slot-${cssKey}-bg2: ${shadeHex(background, SLOT_COLOR_HATCH_SHADE)};`);
      }
    }
    if (text) lines.push(`--jt-slot-${cssKey}-text: ${text};`);
  });

  return lines.join('\n      ');
};

/**
 * Names the slot states whose colours differ between two maps — the summary
 * line for the audit trail.
 *
 * @param {object|undefined} before - the saved config.slotButtonColors
 * @param {object|undefined} after - the one about to be saved
 * @returns {string[]} state labels (e.g. ['Booked', 'Pending']); empty when
 *   nothing changed
 */
export const listChangedSlotStatuses = (before, after) => SLOT_BUTTON_STATUSES
  .filter(({ key }) => SLOT_COLOR_PARTS.some(({ key: part }) => (
    (before?.[key]?.[part] || null) !== (after?.[key]?.[part] || null)
  )))
  .map(({ label }) => label);
